import {
  Box,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { FiRsZoomIn } from '@stocker/ui-components/design-system'
import type React from 'react'
import { useIntl } from 'react-intl'
import type { IBasketItemProps } from '../SystemBasket/SystemBasket'
import { buildKmmiLayout } from './kmmiGrouping'

/**
 * The summary drawing for KombiLine Mantelstein. A configuration holds parts that belong to the
 * whole chimney next to parts that belong to one Zug, which the single column the other
 * configurators use cannot express: the Deck- and Tragplatte close the frame, each Zug gets a
 * column inside it, and the air shaft a narrow one beside them.
 *
 * Parts with no place in a stack stay with the group that owns them, so a zweizügig configuration
 * never merges Zug 1 and Zug 2 into one anonymous pile.
 */

const hideBrokenImage = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = 'none'
}

const Thumb = ({ item, width }: { item: IBasketItemProps; width: string }) => {
  if (!item.image?.fullpath) return null
  return (
    <img
      src={`${item.image.fullpath}?width=200`}
      alt={item.name}
      title={item.name}
      onError={hideBrokenImage}
      style={{ width, maxHeight: '60px', objectFit: 'contain' }}
    />
  )
}

/** Aufpreise carry no product image by design, so the quantity needs the name to hang on. */
const StackEntry = ({ item, width }: { item: IBasketItemProps; width: string }) => (
  <Flex direction="column" align="center" maxW="100%">
    {item.image?.fullpath ? (
      <Thumb item={item} width={width} />
    ) : (
      <Text fontSize="9px" lineHeight="1.15" textAlign="center" noOfLines={2} opacity={0.7}>
        {item.name}
      </Text>
    )}
    {item.quantity > 1 && (
      <Text fontSize="9px" lineHeight="1.1" opacity={0.55}>
        ×{item.quantity}
      </Text>
    )}
  </Flex>
)

/** Edelstahlabdeckplatte and Tragplatte sit on the frame, not inside a column. */
const Plate = ({ item, edge }: { item: IBasketItemProps; edge: 'top' | 'bottom' }) => (
  <Flex
    align="center"
    gap={2}
    px={2}
    py={1}
    borderBottom={edge === 'top' ? '1px solid' : undefined}
    borderTop={edge === 'bottom' ? '1px solid' : undefined}
    borderColor="secondaryText.50"
  >
    <Thumb item={item} width="30px" />
    <Text fontSize="10px" lineHeight="1.2" opacity={0.7} noOfLines={2}>
      {item.name}
    </Text>
  </Flex>
)

const Column = ({
  label,
  stack,
  loose,
  imageWidth,
  flex,
  withDivider,
}: {
  label: string
  stack: IBasketItemProps[]
  loose: IBasketItemProps[]
  imageWidth: string
  flex: string
  withDivider: boolean
}) => (
  <Box
    flex={flex}
    minW={0}
    px={1}
    py={2}
    borderLeft={withDivider ? '1px dashed' : undefined}
    borderColor="secondaryText.50"
  >
    <Text fontSize="9px" textAlign="center" mb={2} opacity={0.6} noOfLines={1}>
      {label}
    </Text>
    <Flex direction="column" align="center" gap={1}>
      {stack.map((item) => (
        <StackEntry key={item.configVariantLineId} item={item} width={imageWidth} />
      ))}
    </Flex>
    {loose.length > 0 && (
      <Flex
        wrap="wrap"
        justify="center"
        gap={1}
        mt={2}
        pt={1}
        borderTop="1px dashed"
        borderColor="secondaryText.50"
      >
        {loose.map((item) => (
          <Flex key={item.configVariantLineId} direction="column" align="center" maxW="100%">
            <Thumb item={item} width="18px" />
            <Text fontSize="8px" lineHeight="1.1" noOfLines={1} opacity={0.6} title={item.name}>
              {item.name}
            </Text>
          </Flex>
        ))}
      </Flex>
    )}
  </Box>
)

/** Mantelstein, Dünnbettmörtel and anything else that belongs to the chimney as a whole. */
const SharedRow = ({ items }: { items: IBasketItemProps[] }) => {
  if (items.length === 0) return null
  return (
    <Flex wrap="wrap" gap={1} mt={2}>
      {items.map((item) => (
        <Flex
          key={item.configVariantLineId}
          align="center"
          gap={1}
          px={1}
          py="2px"
          border="1px solid"
          borderColor="secondaryText.50"
          maxW="100%"
        >
          <Thumb item={item} width="16px" />
          <Text fontSize="9px" lineHeight="1.15" noOfLines={1} opacity={0.7}>
            {item.name}
            {item.quantity > 1 ? ` ×${item.quantity}` : ''}
          </Text>
        </Flex>
      ))}
    </Flex>
  )
}

export const KmmiDrawing = ({ items }: { items: IBasketItemProps[] }) => {
  const { formatMessage } = useIntl()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { topPlate, bottomPlate, airShaft, columns, sharedLoose, imageWidth } =
    buildKmmiLayout(items)

  const enlargeLabel = formatMessage({
    id: 'configurator-drawing--enlarge',
    defaultMessage: 'Darstellung vergrößern',
  })

  const drawing = (
    <Box w="full">
      <Box border="2px solid" borderColor="secondaryText.50">
        {topPlate && <Plate item={topPlate} edge="top" />}
        <Flex>
          {columns.map((column, index) => (
            <Column
              key={column.zug}
              label={formatMessage({ id: 'configurator-zug--header' }, { number: column.zug })}
              stack={column.stack}
              loose={column.loose}
              imageWidth={imageWidth}
              flex="1"
              withDivider={index > 0}
            />
          ))}
          {airShaft.length > 0 && (
            <Column
              label={formatMessage({
                id: 'configurator-air-shaft--header',
                defaultMessage: 'Luft',
              })}
              stack={airShaft}
              loose={[]}
              imageWidth={imageWidth}
              flex="0 0 54px"
              withDivider={columns.length > 0}
            />
          )}
        </Flex>
        {bottomPlate && <Plate item={bottomPlate} edge="bottom" />}
      </Box>
      <SharedRow items={sharedLoose} />
    </Box>
  )

  return (
    <>
      <Box w="full" cursor="zoom-in" onClick={onOpen}>
        <Flex justify="flex-end">
          <IconButton
            aria-label={enlargeLabel}
            title={enlargeLabel}
            icon={<FiRsZoomIn />}
            size="xs"
            variant="ghost"
          />
        </Flex>
        {drawing}
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'full', md: '4xl' }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent aria-label={enlargeLabel}>
          <ModalCloseButton />
          <ModalBody py={10}>
            {/* More width alone only spreads the columns apart. Zoom scales the drawing and its
                layout size, so the modal measures the enlarged box. */}
            <Box sx={{ zoom: 2 }}>{drawing}</Box>
            <Text mt={4} fontWeight="bold" fontSize="xs" textAlign="right">
              {formatMessage({ id: 'configurator--symbolic-representation' })}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
